/* eslint-disable react-hooks/exhaustive-deps */
import { Card, Spin, DatePicker, Select, Row, Col, Grid } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import ContestExclusionManagerModal from "./ContestExclusionManagerModal";
import ContestFilters from "./ContestFilters";
import ContestFormModal from "./ContestFormModal/index";
import ContestTable from "./ContestTable";
import { CONTEST_FORM_MODES } from "./helper";
import { showErrorNotification, showSuccessNotification } from "../../utils/notification";
import { clamp } from "../../utils/responsiveHelpers";
import AnalyticsGenericBarGraph from "../common-utils/GenericBarGraph";

const GLOBAL_CONTEST_PAGE_ANALYTICS_INFO = {
  PUBLIC_CONTEST_CALENDAR: {
    name: "Public Contest Tracker",
    url: "/public/contest-calendar"
  },
  PRIVATE_CALENDAR_ROADMAP: {
    name: "Private Calendar Roadmap",
    url: "/course/private/roadmap"
  }
};

const GlobalContestsManagementDashboard = ({ userConfig, ROOT_URL }) => {
  const BASE_URL = "admin/global-contests";
  const screens = Grid.useBreakpoint();
  const isMobileView = screens.md === false;

  const [contests, setContests] = useState([]);
  const [selectedPageUrl, setSelectedPageUrl] = useState(
    GLOBAL_CONTEST_PAGE_ANALYTICS_INFO.PUBLIC_CONTEST_CALENDAR.url
  );
  const [dailyVisitGraphPoints, setDailyVisitGraphPoints] = useState([]);
  const [googleCalendarButtonAnalytics, setGoogleCalendarButtonAnalytics] = useState(0);
  const [calendarAnalyticsFilter, setCalendarAnalyticsFilter] = useState({
    startDate: dayjs().subtract(1, "month").startOf("day").toDate(),
    endDate: dayjs().endOf("day").toDate()
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isDeletingContest, setIsDeletingContest] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedContest, setSelectedContest] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUpdatingContests, setIsUpdatingContests] = useState(false);
  const [isUpdatingYoutube, setIsUpdatingYoutube] = useState(false);
  const [isContestExclusionManagerModalVisible, setIsContestExclusionManagerModalVisible] =
    useState(false);
  const [contestExclusionPatterns, setContestExclusionPatterns] = useState([]);
  const [isSavingPatterns, setIsSavingPatterns] = useState(false);
  const [contestFilters, setContestFilters] = useState({
    hasYoutubeLinkFilter: "",
    hasYoutubeTimestampsFilter: ""
  });

  const fetchGlobalContests = () => {
    setIsLoading(true);
    const params = {
      page: currentPage,
      limit: pageSize,
      searchQuery: searchQuery,
      ...(contestFilters.hasYoutubeLinkFilter && {
        hasYoutubeLinkFilter: contestFilters.hasYoutubeLinkFilter
      }),
      ...(contestFilters.hasYoutubeTimestampsFilter && {
        hasYoutubeTimestampsFilter: contestFilters.hasYoutubeTimestampsFilter
      })
    };

    axios
      .get(`${ROOT_URL}/${BASE_URL}/list`, { params })
      .then((response) => {
        const { contests, pagination } = response.data;
        setContests(contests);
        setPagination(pagination);
        showSuccessNotification("Contests loaded successfully!");
      })
      .catch((err) => {
        console.error("Error fetching contests:", err);
        showErrorNotification(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchGoogleCalendarAnalytics = () => {
    axios
      .get(`${ROOT_URL}/${BASE_URL}/google-calendar-analytics`, {
        params: { filter: calendarAnalyticsFilter }
      })
      .then((response) => {
        const { totalCount = 0, dateWiseButtonClickAnalyticsGraphPoints = [] } = response.data;
        setGoogleCalendarButtonAnalytics({
          totalCount,
          graphPoints: dateWiseButtonClickAnalyticsGraphPoints
        });
      })
      .catch((err) => {
        console.error("Error fetching Google Calendar analytics:", err);
        showErrorNotification(err);
      });
  };

  const fetchDailyVisitGraphPoints = async () => {
    await axios
      .get(`${ROOT_URL}/${BASE_URL}/daily-visit-graph-points`, {
        params: {
          filter: {
            ...calendarAnalyticsFilter,
            pageUrl: selectedPageUrl
          }
        }
      })
      .then((response) => {
        setDailyVisitGraphPoints(response.data || []);
      })
      .catch((err) => {
        console.error("Error fetching daily visit graph points:", err);
        showErrorNotification(err);
      });
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleEditContest = (contest) => {
    setSelectedContest(contest);
    setIsEditModalVisible(true);
  };

  const handleSaveContest = (contestIdentifier, updateData) => {
    setIsUpdating(true);

    axios
      .post(`${ROOT_URL}/${BASE_URL}/${contestIdentifier}`, updateData)
      .then(() => {
        showSuccessNotification("Contest updated successfully!");
        setIsEditModalVisible(false);
        handleCloseCreateModal();
        setSelectedContest(null);
        fetchGlobalContests();
      })
      .catch((err) => {
        console.error("Error updating contest:", err);
        showErrorNotification(err);
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  const handleDeleteContest = (contestIdentifier) => {
    setIsDeletingContest(true);

    axios
      .delete(`${ROOT_URL}/${BASE_URL}/${contestIdentifier}`)
      .then(() => {
        showSuccessNotification("Contest deleted successfully!");
        fetchGlobalContests(); // Refresh the list
      })
      .catch((err) => {
        console.error("Error deleting contest:", err);
        showErrorNotification(err);
      })
      .finally(() => {
        setIsDeletingContest(false);
      });
  };

  const handleUpdateAllContests = () => {
    setIsUpdatingContests(true);

    axios
      .get(`${ROOT_URL}/${BASE_URL}/update-all-contests`)
      .then(() => {
        showSuccessNotification("All contests updated successfully!");
        fetchGlobalContests();
      })
      .catch((err) => {
        console.error("Error updating all contests:", err);
        showErrorNotification(err);
      })
      .finally(() => {
        setIsUpdatingContests(false);
      });
  };

  const handleUpdateAllYoutubeSolutions = () => {
    setIsUpdatingYoutube(true);

    axios
      .get(`${ROOT_URL}/${BASE_URL}/update-all-youtube-solutions`)
      .then(() => {
        showSuccessNotification("All YouTube solutions updated successfully!");
        fetchGlobalContests();
      })
      .catch((err) => {
        console.error("Error updating YouTube solutions:", err);
        showErrorNotification(err);
      })
      .finally(() => {
        setIsUpdatingYoutube(false);
      });
  };

  const handleOpenContestExclusionManagerModal = () => {
    axios
      .get(`${ROOT_URL}/${BASE_URL}/exclusion-patterns`)
      .then((response) => {
        setContestExclusionPatterns(response.data?.patterns || []);
        setIsContestExclusionManagerModalVisible(true);
      })
      .catch(showErrorNotification);
  };

  const handleSaveContestExclusionPatterns = (patterns) => {
    setIsSavingPatterns(true);
    axios
      .post(`${ROOT_URL}/${BASE_URL}/exclusion-patterns`, { patterns })
      .then(() => {
        showSuccessNotification("Exclusion patterns updated successfully!");
        setIsContestExclusionManagerModalVisible(false);
        fetchGlobalContests();
      })
      .catch(showErrorNotification)
      .finally(() => setIsSavingPatterns(false));
  };

  const handleOpenCreateContestModal = () => {
    setSelectedContest(null);
    setIsCreateModalVisible(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalVisible(false);
    setSelectedContest(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalVisible(false);
    setSelectedContest(null);
  };

  const handleCloseContestExclusionManagerModal = () => {
    setIsContestExclusionManagerModalVisible(false);
  };

  useEffect(() => {
    fetchGlobalContests();
  }, [currentPage, pageSize, searchQuery, contestFilters]);

  useEffect(() => {
    fetchGoogleCalendarAnalytics();
    fetchDailyVisitGraphPoints();
  }, [calendarAnalyticsFilter, selectedPageUrl]);

  return (
    <div style={{ padding: clamp(12, 20) }}>
      <ContestFilters
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onUpdateAllContests={handleUpdateAllContests}
        onUpdateAllYoutubeSolutions={handleUpdateAllYoutubeSolutions}
        handleOpenCreateContestModal={handleOpenCreateContestModal}
        handleOpenContestExclusionManagerModal={handleOpenContestExclusionManagerModal}
        isUpdatingContests={isUpdatingContests}
        isUpdatingYoutube={isUpdatingYoutube}
        userConfig={userConfig}
        contestFilters={contestFilters}
        onContestFiltersChange={setContestFilters}
      />

      <Row gutter={[16, 12]} align="middle" style={{ marginBottom: "20px" }}>
        <Col
          xs={24}
          sm={12}
          style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px" }}
        >
          <span>Analytics Date Range:</span>
          <DatePicker.RangePicker
            style={{ width: "min(260px, 100%)" }}
            value={
              calendarAnalyticsFilter.startDate && calendarAnalyticsFilter.endDate
                ? [dayjs(calendarAnalyticsFilter.startDate), dayjs(calendarAnalyticsFilter.endDate)]
                : []
            }
            onChange={(dates) => {
              if (!dates) {
                setCalendarAnalyticsFilter({
                  startDate: null,
                  endDate: null
                });
                return;
              }
              setCalendarAnalyticsFilter({
                startDate: dates[0].clone().startOf("day").toDate(),
                endDate: dates[1].clone().endOf("day").toDate()
              });
            }}
          />
        </Col>
        <Col
          xs={24}
          sm={12}
          style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px" }}
        >
          <span>Select Page:</span>
          <Select
            style={{ width: "min(260px, 100%)" }}
            value={selectedPageUrl}
            onChange={(value) => setSelectedPageUrl(value)}
            options={Object.values(GLOBAL_CONTEST_PAGE_ANALYTICS_INFO).map((page) => ({
              label: page.name,
              value: page.url
            }))}
          />
        </Col>
      </Row>

      <Card style={{ marginTop: 20, marginBottom: 20 }} bordered={true}>
        <h3>Daily Visit Logged-in Users Count</h3>
        <p style={{ marginBottom: "10px" }}>
          This graph shows the total visits on Calendar aggregated by date. This count includes all
          page loads and refreshes from logged-in users.
        </p>
        <AnalyticsGenericBarGraph
          dataPoints={dailyVisitGraphPoints?.loggedInTotalVisitsGraphPoints}
        />
      </Card>

      <Card style={{ marginTop: 20, marginBottom: 20 }} bordered={true}>
        <h3>Unique Daily Logged-in Users Visit Count</h3>
        <p style={{ marginBottom: "10px" }}>
          This graph shows unique user visits per day. Even if a user visits the page multiple
          times, it counts as one per day.
        </p>
        <AnalyticsGenericBarGraph
          dataPoints={dailyVisitGraphPoints?.loggedInUniqueVisitsGraphPoints}
        />
      </Card>

      <Card style={{ marginTop: 20, marginBottom: 20 }} bordered={true}>
        <h3>Daily Visit Non-logged-in Users Count</h3>
        <p style={{ marginBottom: "10px" }}>
          This graph shows total visits by non-logged-in users, aggregated by date. Includes all
          page loads and refreshes.
        </p>
        <AnalyticsGenericBarGraph
          dataPoints={dailyVisitGraphPoints?.nonLoggedInTotalVisitsGraphPoints}
        />
      </Card>

      <Card style={{ marginTop: 20, marginBottom: 20 }} bordered={true}>
        <h3>Total Clicks: {googleCalendarButtonAnalytics.totalCount}</h3>
        <h3>Daily Google Calendar Button Clicks</h3>
        <p style={{ marginBottom: "10px" }}>
          This graph shows how many times users clicked the <b>Add to Calendar</b> button each day.
          This represents button clicks, not the exact count of users who successfully synced the
          events to their calendars.
        </p>
        <AnalyticsGenericBarGraph dataPoints={googleCalendarButtonAnalytics.graphPoints} />
      </Card>

      {isLoading && !isMobileView ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
            width: "100%"
          }}
        >
          <Spin size="large" tip="Loading contests..." />
        </div>
      ) : (
        <ContestTable
          contests={contests}
          pagination={pagination}
          currentPage={currentPage}
          pageSize={pageSize}
          isLoading={isLoading}
          onPageChange={handlePageChange}
          onEditContest={handleEditContest}
          onDeleteContest={handleDeleteContest}
          isDeletingContest={isDeletingContest}
          userConfig={userConfig}
        />
      )}

      {isEditModalVisible && selectedContest && (
        <ContestFormModal
          mode={CONTEST_FORM_MODES.UPDATE}
          isVisible={isEditModalVisible}
          onCancel={handleCloseEditModal}
          onSave={handleSaveContest}
          contest={selectedContest}
          isLoading={isUpdating}
        />
      )}

      {isCreateModalVisible && (
        <ContestFormModal
          mode={CONTEST_FORM_MODES.CREATE}
          isVisible={isCreateModalVisible}
          onCancel={handleCloseCreateModal}
          onSave={handleSaveContest}
          contest={selectedContest}
          isLoading={isUpdating}
        />
      )}

      {isContestExclusionManagerModalVisible && (
        <ContestExclusionManagerModal
          isVisible={isContestExclusionManagerModalVisible}
          onCancel={handleCloseContestExclusionManagerModal}
          onSave={handleSaveContestExclusionPatterns}
          initialPatterns={contestExclusionPatterns}
          isLoading={isSavingPatterns}
        />
      )}
    </div>
  );
};

export default GlobalContestsManagementDashboard;
